/**
 * Media URL Resolver
 *
 * Resolves media URLs for rendering, preferring Convex URLs over blob URLs.
 * This ensures the renderer receives valid https:// URLs instead of blob: URLs.
 */

import { getUserId } from "./user-id";
import { getUserMediaItems, UserMediaItem } from "./indexdb";
import { Overlay } from "../../types";

/**
 * Result of URL resolution
 */
export interface ResolvedUrl {
  src: string;
  warning?: string;
}

/**
 * Result of resolving all overlays
 */
export interface ResolvedOverlays {
  overlays: Overlay[];
  warnings: string[];
}

/**
 * Options for URL resolution
 */
export interface ResolveOptions {
  /**
   * If true, throws an error if any blob URLs remain after resolution
   * Useful for critical operations like server-side rendering where blob URLs will fail
   */
  strictMode?: boolean;
}

/**
 * Service for resolving media URLs before rendering
 */
export class MediaUrlResolver {
  /**
   * Resolve all overlays in a composition
   *
   * Converts blob URLs to Convex URLs where possible
   *
   * @param overlays - Array of overlays to resolve
   * @param options - Resolution options (e.g., strictMode)
   * @returns Resolved overlays and any warnings
   * @throws Error if strictMode is enabled and blob URLs remain after resolution
   */
  static async resolveAllOverlays(
    overlays: Overlay[],
    options: ResolveOptions = {}
  ): Promise<ResolvedOverlays> {
    const warnings: string[] = [];
    const blobUrls: string[] = [];

    const resolvedOverlays = await Promise.all(
      overlays.map(async (overlay) => {
        if ("src" in overlay && overlay.src) {
          const { src, warning } = await this.resolveOverlaySrc(overlay.src, overlay);
          if (warning) {
            warnings.push(warning);
          }

          // Track blob URLs that remain after resolution
          if (src.startsWith("blob:")) {
            blobUrls.push(this.getOverlayName(overlay));
          }

          return { ...overlay, src };
        }
        return overlay;
      })
    );

    // In strict mode, throw error if any blob URLs remain
    if (options.strictMode && blobUrls.length > 0) {
      throw new Error(
        `Cannot render with blob URLs. ${blobUrls.length} overlay(s) still have blob URLs: ${blobUrls.join(", ")}. ` +
          `Please wait for file uploads to complete or re-upload the files.`
      );
    }

    return { overlays: resolvedOverlays, warnings };
  }

  /**
   * Resolve a single overlay's src URL
   *
   * @param src - Original src URL
   * @param overlay - Overlay object (for warning messages)
   * @returns Resolved URL and optional warning
   */
  private static async resolveOverlaySrc(src: string, overlay: Overlay): Promise<ResolvedUrl> {
    console.log(`[Resolver] Resolving URL for overlay ${this.getOverlayName(overlay)}: ${src.substring(0, 50)}...`);

    // Already using https:// Convex URL - perfect!
    if (src.startsWith("https://") && src.includes("convex.cloud")) {
      console.log(`[Resolver] ✓ Already using Convex URL`);
      return { src };
    }

    // Already using https:// (other source like Pexels)
    if (src.startsWith("https://") || src.startsWith("http://")) {
      console.log(`[Resolver] ✓ Already using valid HTTP(S) URL`);
      return { src };
    }

    // Check if we have Convex URL in IndexedDB
    console.log(`[Resolver] Searching IndexedDB for matching media...`);
    const mediaItem = await this.findMediaBySrc(src);

    if (mediaItem) {
      console.log(`[Resolver] Found media item: ${mediaItem.name}, convexUrl: ${mediaItem.convexUrl ? 'YES' : 'NO'}, uploadStatus: ${mediaItem.uploadStatus}`);

      if (mediaItem.convexUrl) {
        console.log(`[Resolver] ✓ Using Convex URL for overlay: ${this.getOverlayName(overlay)}`);
        return { src: mediaItem.convexUrl };
      } else {
        console.warn(`[Resolver] ⚠ Media found but no Convex URL yet (status: ${mediaItem.uploadStatus})`);
      }
    } else {
      console.warn(`[Resolver] ⚠ No matching media found in IndexedDB`);
    }

    // Blob URL - show warning
    if (src.startsWith("blob:")) {
      const overlayName = this.getOverlayName(overlay);
      console.warn(`[Resolver] ⚠ Using blob URL (upload may still be in progress)`);
      return {
        src,
        warning: `Overlay "${overlayName}" uses temporary blob URL. Rendering may fail if file is not uploaded to Convex.`,
      };
    }

    // API path - convert to absolute URL
    if (src.startsWith("/api/")) {
      const absoluteUrl = `${window.location.origin}${src}`;
      console.log(`[Resolver] Converting API path to absolute URL`);
      return { src: absoluteUrl };
    }

    // Fallback to original
    console.warn(`[Resolver] ⚠ Using original URL (no resolution applied)`);
    return { src };
  }

  /**
   * Find media item in IndexedDB by src path
   *
   * @param src - Source path to search for
   * @returns Media item or null if not found
   */
  private static async findMediaBySrc(src: string): Promise<UserMediaItem | null> {
    try {
      const userId = getUserId();
      const allMedia = await getUserMediaItems(userId);

      // Search by serverPath or convexUrl
      return (
        allMedia.find((item) => item.serverPath === src || item.convexUrl === src) || null
      );
    } catch (error) {
      console.error("[Resolver] Error finding media by src:", error);
      return null;
    }
  }

  /**
   * Get overlay name for warning messages
   *
   * @param overlay - Overlay object
   * @returns Human-readable overlay name
   */
  private static getOverlayName(overlay: Overlay): string {
    if ("content" in overlay && overlay.content) {
      return overlay.content.toString();
    }
    if ("name" in overlay && overlay.name) {
      return overlay.name;
    }
    return `Overlay ${overlay.id}`;
  }

  /**
   * Check if an overlay has a blob URL
   *
   * @param overlay - Overlay to check
   * @returns True if overlay uses blob URL
   */
  static hasBlobUrl(overlay: Overlay): boolean {
    if ("src" in overlay && overlay.src) {
      return overlay.src.startsWith("blob:");
    }
    return false;
  }

  /**
   * Count overlays with blob URLs
   *
   * @param overlays - Array of overlays
   * @returns Number of overlays using blob URLs
   */
  static countBlobUrls(overlays: Overlay[]): number {
    return overlays.filter((overlay) => this.hasBlobUrl(overlay)).length;
  }
}
